import {
  CandidateEntity,
  CompanyEntity,
  HiringAuthorityEntity,
  JobOrderEntity
} from 'UI/constants/entityTypes';

import { buildResolvedEmailBody } from '../../forms/EmailDetails/EmailDetails.utils';
import { prepareSendoutPayload } from '../CreateSendoutDrawer/CreateSendoutDrawer.utils';
import { FORM_FIELDS_MAP } from '../CreateSendoutDrawer/SendoutForm/SendoutForm.constants';

import {
  SENDOUT_CONVERSION_FIELDS_MAP,
  SWITCH_SENDOVER_TO_SENDOUT_TYPES
} from './ConvertSendoverToSendoutDrawer.constants';

const TEMPLATE_TAG_PATTERN = /{{\s*[\w_]+\s*}}/;

const normalizeRecruiter = recruiter => {
  if (!recruiter?.id) return null;

  return {
    ...recruiter,
    fullName: recruiter?.fullName || recruiter?.personalInformation?.full_name || recruiter?.title
  };
};

const normalizeCandidate = candidate => {
  if (!candidate?.id) return null;

  return {
    ...candidate,
    fullName: candidate?.fullName || candidate?.personalInformation?.full_name || candidate?.title,
    email: candidate?.email || candidate?.personal_email || candidate?.personalEmail || null
  };
};

const normalizeHiringAuthority = hiringAuthority => {
  if (!hiringAuthority?.id) return null;

  return hiringAuthority;
};

const normalizeEmailItems = items => {
  if (!Array.isArray(items)) return [];

  return items.reduce((normalizedItems, item) => {
    if (typeof item === 'string' && item.trim()) {
      normalizedItems.push({ work_email: item.trim() });
      return normalizedItems;
    }

    if (!item || typeof item !== 'object') return normalizedItems;

    const email = item.work_email || item.email;

    if (!email) return normalizedItems;

    normalizedItems.push({
      ...item,
      work_email: email
    });

    return normalizedItems;
  }, []);
};

const getSendoverEmailDetails = sendover => sendover?.emailDetails || {};
const hasTemplateTags = value => typeof value === 'string' && TEMPLATE_TAG_PATTERN.test(value);
const getDisplayValue = value => value || '---';

const getPersonDisplayName = person => {
  if (!person) return '';

  const fullName =
    person?.fullName ||
    person?.full_name ||
    person?.title ||
    person?.name ||
    [person?.first_name, person?.last_name].filter(Boolean).join(' ');

  return fullName || person?.work_email || person?.email || '';
};

export const buildWithoutMailConfirmationSummary = ({ formData = {}, jobOrder = {} }) => {
  const hiringAuthorityValue = formData[FORM_FIELDS_MAP.HiringAuthority.key];
  const hiringAuthorityDisplayValue = getPersonDisplayName(hiringAuthorityValue);

  return [
    {
      label: CompanyEntity.singular,
      value: getDisplayValue(jobOrder?.company?.name)
    },
    {
      label: JobOrderEntity.singular,
      value: getDisplayValue(jobOrder?.title)
    },
    {
      label: HiringAuthorityEntity.singular,
      value: getDisplayValue(hiringAuthorityDisplayValue)
    },
    {
      label: CandidateEntity.singular,
      value: getDisplayValue(getPersonDisplayName(formData[FORM_FIELDS_MAP.Candidate.key]))
    }
  ];
};

export const buildConvertSendoutDefaultValues = ({ sendover = {}, switchType }) => {
  const emailDetails = getSendoverEmailDetails(sendover);
  const isWithoutMail = switchType === SWITCH_SENDOVER_TO_SENDOUT_TYPES.WithoutMail;
  const initialEmailBody =
    emailDetails?.template || sendover?.template || sendover?.email_body || '';

  return {
    [FORM_FIELDS_MAP.AccountableRecruiter.key]:
      normalizeRecruiter(
        sendover?.jobOrderAccountable || sendover?.joborder?.recruiter || sendover?.recruiter
      ) || null,
    [FORM_FIELDS_MAP.Candidate.key]: normalizeCandidate(sendover?.candidate),
    [FORM_FIELDS_MAP.CandidateRecruiter.key]:
      normalizeRecruiter(sendover?.candidateAccountable || sendover?.candidate?.recruiter) || null,
    [FORM_FIELDS_MAP.HiringAuthority.key]:
      normalizeHiringAuthority(sendover?.hiringAuthority || sendover?.hiring_authority) || null,
    [FORM_FIELDS_MAP.CancelEmailToHiringAuthority.key]: isWithoutMail,
    [FORM_FIELDS_MAP.CarbonCopy.key]: normalizeEmailItems(
      emailDetails?.cc_emails || sendover?.cc_emails
    ),
    [FORM_FIELDS_MAP.BlindCarbonCopy.key]: normalizeEmailItems(
      emailDetails?.bcc_emails || sendover?.bcc_emails
    ),
    [FORM_FIELDS_MAP.EmailSubject.key]: emailDetails?.subject || sendover?.subject || '',
    ...(hasTemplateTags(initialEmailBody)
      ? { [FORM_FIELDS_MAP.EmailBody.key]: initialEmailBody }
      : {}),
    [FORM_FIELDS_MAP.ResolvedEmailBody.key]: ''
  };
};

export const buildConvertSendoverToSendoutPayload = ({ formData = {}, switchType }) =>
  prepareSendoutPayload({
    formData,
    fieldsMap: SENDOUT_CONVERSION_FIELDS_MAP,
    canSendEmail: switchType !== SWITCH_SENDOVER_TO_SENDOUT_TYPES.WithoutMail
  });

export const buildConvertSendoverToSendoutSubmissionData = ({
  formData = {},
  jobOrder = {},
  candidateInfo = {},
  switchType
}) => {
  const isWithoutMail = switchType === SWITCH_SENDOVER_TO_SENDOUT_TYPES.WithoutMail;
  const confirmation = {
    resolvedEmailBody: '',
    resolvedTagValues: [],
    summaryItems: []
  };

  if (isWithoutMail) {
    confirmation.summaryItems = buildWithoutMailConfirmationSummary({ formData, jobOrder });
  } else {
    Object.assign(
      confirmation,
      buildResolvedEmailBody({
        emailBody: formData[FORM_FIELDS_MAP.EmailBody.key],
        hiringAuthority: formData[FORM_FIELDS_MAP.HiringAuthority.key],
        candidate: formData[FORM_FIELDS_MAP.Candidate.key],
        interviewData: {
          interviewDate: formData[FORM_FIELDS_MAP.InterviewDate.key],
          interviewFromHour: formData[FORM_FIELDS_MAP.InterviewFromHour.key],
          interviewToHour: formData[FORM_FIELDS_MAP.InterviewToHour.key],
          interviewTimeFrame: formData[FORM_FIELDS_MAP.InterviewTimeFrame.key],
          interviewTimezone: formData[FORM_FIELDS_MAP.InterviewTimezone.key]
        },
        jobOrder,
        candidateInfo
      })
    );
  }

  return {
    payload: buildConvertSendoverToSendoutPayload({ formData, switchType }),
    confirmation,
    closeAll: true
  };
};
